import { type Component, createMemo, createSignal, getOwner, runWithOwner, Show, Suspense, untrack } from "solid-js";
import FormatDate from "../../../components/formatting/FormatDate";
import ApiService from "../../../services/ApiService";
import type { TranslationKey } from "../../../state/i18n";
import { useTranslation } from "../../../state/i18n";
import type CustomerId from "../../../state/CustomerId";
import type FormId from "../../../state/FormId";
import type { Submission } from "../../../types/Submission";
import { compact, isEmpty, isNil } from "lodash-es";
import { condition } from "../../../utils/solidUtils";
import { hasData, toField } from "../../../fields/fields";
import { getIconCategoryColor } from "../../../utils/iconColorUtil";
import type FormVersionId from "../../../state/FormVersionId";
import { Scope } from "../../../types";
import EmptyState from "../../../components/emptyState/EmptyState";
import type { ColumnDefWithId } from "./Table";
import Table, {
  TableActiveFilters,
  TableBody,
  TableBulkSelectBar,
  TableColumnSettings,
  TablePagination,
} from "./Table";
import TableFilterButton from "./TableFilterButton";
import SubmissionId from "../../../state/SubmissionId";
import { defaultStringFilter } from "./filter";
import { integerValue, requireText } from "../../../utils/validation/base";
import { MAX_CHARACTERS_XL, MAX_NUMBER_XL, MINIMUM_ACTION_LOADING_DELAY } from "../../../constants";
import { renderFieldValue } from "../../../fields/fieldValueRenderer";
import { useQueryClient } from "@tanstack/solid-query";
import { TextButton } from "../../../components/button/Button";
import { triggerBrowserDownload } from "../../../utils/uploadUtil";
import { useClient } from "../../../state/client";
import ExportSubmissionsButton from "./ExportSubmissionsButton";
import { useModalManager } from "../../../components/modal/ModalManager";
import DeleteSubmissionsModal from "../folder/DeleteSubmissionsModal";
import HookPipelineModal from "../folder/HookPipelineModal";
import type { IconButtonMenuProps } from "../../../components/listItemMenu/ListItemMenu";
import { SplitButtonMenu } from "../../../components/listItemMenu/ListItemMenu";
import ViewSubmissionModal from "./ViewSubmissionModal";
import pMinDelay from "p-min-delay";
import { TableProvider, useTable } from "./TableProvider";
import { keyFromSelector } from "i18next";
import PermissionService from "../../../services/PermissionService.ts";
import { hasEmailWarnings } from "../../../utils/mailStatusUtil";
import {
  deserializeConfigurationStore,
  serializeConfigurationStore,
} from "../../../utils/viewConfigurationStoreUtil.ts";

const integrationsColumnId = "integrations-column";

type SubmissionTableProps = {
  customerId: CustomerId;
  formId: FormId;
  formVersionId: FormVersionId | undefined;
  formName: string;
};

const STATUS_MAP: Record<
  string,
  { status: "none" | "success" | "warning" | "danger" | "neutral"; label: TranslationKey }
> = {
  OPEN: { status: "neutral", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_OPEN) },
  IN_PROGRESS: { status: "neutral", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_IN_PROGRESS) },
  RETRY: { status: "danger", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_RETRY) },
  SUCCESS: { status: "success", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_SUCCESS) },
  BLOCKED: { status: "danger", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_BLOCKED) },
  OUT_OF_CREDITS: { status: "neutral", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_OUT_OF_CREDITS) },
};

const getStatus = (
  submission: Submission,
): { status: "none" | "success" | "warning" | "danger" | "neutral"; label: TranslationKey } => {
  if (submission.integrationStatus === "SUCCESS" && hasEmailWarnings(submission.mailStatuses ?? [])) {
    return { status: "warning", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_WARNING) };
  }

  return (
    STATUS_MAP[submission.integrationStatus] ?? {
      status: "danger",
      label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_OPEN),
    }
  );
};

const defaultColumns = (openHookPipelineModal: (submission: Submission) => void) => {
  const { t } = useTranslation();
  return [
    {
      id: integrationsColumnId,
      accessorKey: integrationsColumnId,
      meta: {
        name: t(($) => $.SUBMISSIONS_TABLE_COLUMNS_INTEGRATIONS_HEADER),
        icon: {
          name: "puzzle",
          colors: "bg-surface-subtle icon-primary",
        },
        filter: {
          type: "none",
          definition: [],
        },
      },
      cell: (context) => {
        const { status, label } = getStatus(context.row.original);
        return (
          <TextButton
            status={status}
            text={t(label)}
            variant="secondary"
            size="medium"
            onClick={() => openHookPipelineModal(context.row.original)}
          />
        );
      },
    } satisfies ColumnDefWithId<Submission>,
  ];
};

const SubmissionTable: Component<SubmissionTableProps> = (props) => (
  <TableProvider>
    <SubmissionTableBody {...props} />
  </TableProvider>
);

const SubmissionTableBody: Component<SubmissionTableProps> = (props) => {
  const owner = getOwner();
  const queryClient = useQueryClient();
  const { open } = useModalManager();
  const table = useTable();
  const canDeleteSubmissions = createMemo(() => PermissionService.canDeleteSubmission(props.customerId, props.formId));

  const formVersionQuery = createMemo(() => {
    if (isNil(props.formVersionId)) {
      return {
        data: null,
      };
    } else {
      return ApiService.getFormVersionQuery(props.formId, props.formVersionId);
    }
  });

  const setIsHookPipelineModalOpen = (submission: Submission) =>
    open({
      content: () => (
        <HookPipelineModal
          formId={props.formId}
          submissionId={submission.id}
          mailStatuses={submission.mailStatuses ?? []}
        />
      ),
      onClose: () => {
        void queryClient.invalidateQueries({ queryKey: ["submissions", props.formId.toString()] });
      },
    });

  return (
    <Suspense>
      <Show
        when={condition(formVersionQuery().data, (formVersion) => !isNil(formVersion))}
        fallback={<NoSubmissions />}
      >
        {(formVersion) => {
          const { t } = useTranslation();

          const rootFieldsWithData = createMemo(() =>
            formVersion()
              .fields.filter((fieldConfig) => fieldConfig.scope === Scope.ROOT)
              .filter(hasData),
          );
          const columns = createMemo<ColumnDefWithId<Submission>[]>(() => [
            ...defaultColumns(setIsHookPipelineModalOpen),
            {
              id: "info.userId",
              accessorKey: "info.username",
              enableSorting: true,
              enableColumnFilter: true,
              meta: {
                name: t(($) => $.SUBMISSIONS_TABLE_COLUMNS_SUBMITTED_BY_HEADER),
                icon: {
                  name: "user",
                  colors: "bg-surface-subtle icon-primary",
                },
                filter: {
                  type: "string",
                  definition: [
                    {
                      operator: "is",
                      inputType: "text",
                      schema: requireText(MAX_CHARACTERS_XL),
                    },
                    {
                      operator: "isNot",
                      inputType: "text",
                      schema: requireText(MAX_CHARACTERS_XL),
                    },
                    {
                      operator: "contains",
                      inputType: "text",
                      schema: requireText(MAX_CHARACTERS_XL),
                    },
                    {
                      operator: "startsWith",
                      inputType: "text",
                      schema: requireText(MAX_CHARACTERS_XL),
                    },
                    {
                      operator: "endsWith",
                      inputType: "text",
                      schema: requireText(MAX_CHARACTERS_XL),
                    },
                  ],
                },
              },
            },
            {
              id: "info.date",
              accessorKey: "info.date",
              enableSorting: true,
              enableColumnFilter: true,
              cell: (context) => <FormatDate value={context.getValue<Date>()} />,
              meta: {
                name: t(($) => $.SUBMISSIONS_TABLE_COLUMNS_SUBMITTED_ON_HEADER),
                icon: {
                  name: "calendar",
                  colors: "bg-surface-subtle icon-primary",
                },
                filter: {
                  type: "date",
                  definition: [
                    { operator: "is", inputType: "datetime" },
                    { operator: "isBefore", inputType: "datetime" },
                    { operator: "isAfter", inputType: "datetime" },
                  ],
                },
              },
            },
            {
              id: "meta.serialNumber",
              accessorKey: "meta.serialNumber",
              enableSorting: true,
              enableColumnFilter: true,
              meta: {
                name: t(($) => $.SUBMISSIONS_TABLE_COLUMNS_SERIAL_NUMBER_HEADER),
                icon: {
                  name: "tag",
                  colors: "bg-surface-subtle icon-primary",
                },
                filter: {
                  type: "number",
                  definition: [
                    {
                      operator: "is",
                      inputType: "number",
                      allowDecimal: false,
                      schema: integerValue(1, MAX_NUMBER_XL),
                    },
                    {
                      operator: "greaterThan",
                      inputType: "number",
                      allowDecimal: false,
                      schema: integerValue(1, MAX_NUMBER_XL),
                    },
                    {
                      operator: "lessThan",
                      inputType: "number",
                      allowDecimal: false,
                      schema: integerValue(1, MAX_NUMBER_XL),
                    },
                  ],
                },
              },
            },
            {
              id: "info.description",
              accessorKey: "info.summary",
              enableSorting: true,
              enableColumnFilter: true,
              meta: {
                name: t(($) => $.SUBMISSIONS_TABLE_COLUMNS_SUMMARY_HEADER),
                icon: {
                  name: "viewList",
                  colors: "bg-surface-subtle icon-primary",
                },
                filter: {
                  type: "string",
                  definition: defaultStringFilter,
                },
              },
            },
            ...rootFieldsWithData().map((fieldConfig) => {
              const baseField = toField(fieldConfig.type);

              return {
                id: `data.${fieldConfig.properties.data_name}`,
                accessorFn: (submission) => submission.data[fieldConfig.properties.data_name],
                enableSorting: true,
                enableColumnFilter: !isEmpty(baseField.filter!.definition),
                cell: (context) => {
                  const value = context.getValue();
                  return renderFieldValue(value, fieldConfig, formVersion());
                },
                meta: {
                  name: baseField.displayLabel(fieldConfig.properties),
                  icon: {
                    name: baseField.icon,
                    colors: getIconCategoryColor(baseField.category),
                  },
                  filter: baseField.filter!,
                },
              } satisfies ColumnDefWithId<Submission>;
            }),
          ]);

          return (
            <Table<Submission>
              columns={columns()}
              persistKey={`submissions-${props.formId.toString()}`}
              serializeConfigurationStore={serializeConfigurationStore(formVersion())}
              deserializeConfigurationStore={deserializeConfigurationStore(formVersion())}
              defaultVisibleColumns={[
                integrationsColumnId,
                "info.userId",
                "info.date",
                "meta.serialNumber",
                "info.description",
              ]}
              defaultSorting={[{ id: "info.date", desc: true }]}
              fetchData={(options) => ApiService.getSubmissions(props.formId, options)}
              bulkSelect={canDeleteSubmissions()}
              bulkSelectActions={[
                {
                  icon: "trash",
                  text: t(($) => $.DELETE_BUTTON),
                  onClick: (submissionIds) => {
                    open({
                      content: () => (
                        <DeleteSubmissionsModal
                          formName={props.formName}
                          submissionIds={submissionIds.map((submissionId) => SubmissionId.from(submissionId))}
                          formId={props.formId}
                          onSuccess={() => table.setRowSelection({})}
                        />
                      ),
                    });
                  },
                },
              ]}
              actionButton={(context) => {
                const resendSubmissionMutation = ApiService.resendSubmission();
                const [resendingSubmission, setResendingSubmission] = createSignal(false);

                const actions = createMemo<IconButtonMenuProps<string>["items"]>(() => [
                  compact([
                    {
                      value: "download-files",
                      type: "action",
                      label: t(($) => $.SUBMISSIONS_TABLE_ACTIONS_DOWNLOAD_SUBMISSION_FILES),
                      variant: "icon",
                      icon: { name: "download", type: "outline", class: "icon-secondary" },
                      onClick: () => {
                        runWithOwner(owner, async () => {
                          const client = useClient();
                          const { secureUri } = await ApiService.getDownloadSubmissionFilesUri(
                            untrack(() => props.customerId),
                            untrack(() => props.formId),
                            SubmissionId.from(context.row.id),
                          );
                          void triggerBrowserDownload(client, secureUri);
                        });
                      },
                    },
                    PermissionService.canResendSubmission(props.customerId, props.formId) && {
                      value: "resend",
                      type: "action",
                      label: t(($) => $.SUBMISSIONS_TABLE_ACTIONS_RESEND_SUBMISSION),
                      variant: "icon",
                      icon: { name: "refresh", type: "outline", class: "icon-secondary" },
                      onClick: async () => {
                        setResendingSubmission(true);
                        pMinDelay(
                          resendSubmissionMutation.mutateAsync(SubmissionId.from(context.row.id)),
                          MINIMUM_ACTION_LOADING_DELAY,
                        ).finally(() => setResendingSubmission(false));
                      },
                    },
                    canDeleteSubmissions() && {
                      value: "delete",
                      type: "action",
                      label: t(($) => $.DELETE_BUTTON),
                      variant: "icon",
                      icon: { name: "trash", type: "outline", class: "icon-secondary" },
                      onClick: () => {
                        open({
                          content: () => (
                            <DeleteSubmissionsModal
                              formName={props.formName}
                              submissionIds={[SubmissionId.from(context.row.id)]}
                              formId={props.formId}
                              onSuccess={(submissionIds) =>
                                table.setRowSelection((prev) => {
                                  const newRowSelection = { ...prev };
                                  delete newRowSelection[submissionIds[0].toString()];
                                  return newRowSelection;
                                })
                              }
                            />
                          ),
                        });
                      },
                    },
                  ]),
                ]);

                return (
                  <SplitButtonMenu
                    label={t(($) => $.TABLE_COLUMNS_ACTION_BUTTON_LABEL)}
                    loading={resendingSubmission()}
                    actionButtonProps={{
                      onClick: () => {
                        open({
                          content: () => (
                            <ViewSubmissionModal
                              formName={props.formName}
                              formVersion={formVersion()}
                              submission={context.row.original}
                              fields={rootFieldsWithData()}
                            />
                          ),
                        });
                      },
                    }}
                    menuButtonProps={{
                      "aria-label": t(($) => $.ACTIONS_ARIA),
                    }}
                    items={actions()}
                    status="none"
                  />
                );
              }}
              emptyState={<NoSubmissions />}
            >
              <div class="flex gap-4">
                <TableFilterButton />
                <div class="ml-auto flex gap-2">
                  <TableColumnSettings />
                  <ExportSubmissionsButton formVersion={formVersion()} />
                </div>
              </div>
              <TableActiveFilters />
              <div class="mt-7 flex min-h-0 flex-col">
                <TableBody
                  emptyFilteredText={keyFromSelector(($) => $.SUBMISSIONS_TABLE_EMPTY_FILTERED_DESCRIPTION)}
                  loadingText={keyFromSelector(($) => $.SUBMISSIONS_TABLE_LOADING_DESCRIPTION)}
                />
              </div>
              <TableBulkSelectBar />
              <TablePagination pageSizeLabel={keyFromSelector(($) => $.SUBMISSIONS_TABLE_PAGE_SIZE_LABEL)} />
            </Table>
          );
        }}
      </Show>
    </Suspense>
  );
};

const NoSubmissions = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      title={t(($) => $.SUBMISSIONS_TABLE_EMPTY_NONE_TITLE)}
      description={t(($) => $.SUBMISSIONS_TABLE_EMPTY_NONE_DESCRIPTION)}
      illustration={{ primaryIcon: "document", secondaryIcon: "search" }}
    />
  );
};

export default SubmissionTable;
